import { useId, useMemo, useState } from 'react';
import { createDonationCheckout } from '../api/payments';
import {
  LIMITS,
  computeDonationBreakdown,
  formatMoney,
  validateDonationRequest,
} from '../lib/payments';
import {
  FREQUENCY_LABELS,
  METHOD_HINTS,
  METHOD_LABELS,
  parseDollarsToCents,
} from '../lib/paymentsUi';
import { ui, cn } from '../lib/ui';
import DonationBreakdown from './DonationBreakdown';

/**
 * The donor's form for one organization (docs/Decisions.md D44): amount
 * presets and, where the org allows it, a custom amount; frequency and bank
 * payment where the plan allows them; the donor-covers-fees toggle; the full
 * breakdown; then a redirect to Stripe Checkout.
 *
 * @param {object} props.page - GET /billing/donate/{orgSlug} data (available = true)
 * @param {string} props.orgSlug
 * @param {boolean} props.signedIn
 */
export default function DonateForm({ page, orgSlug, signedIn }) {
  const ids = useId();
  const presets = useMemo(() => page.presetAmountsCents || [], [page]);
  const [selected, setSelected] = useState(presets[1] ?? presets[0] ?? null);
  const [customText, setCustomText] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [frequency, setFrequency] = useState('one_time');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [coverFees, setCoverFees] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const choosableMethods = page.paymentMethods.filter(m => m !== 'link');
  const amountCents = useCustom ? parseDollarsToCents(customText) : selected;

  const { breakdown, problems } = useMemo(() => {
    const settings = {
      presetAmountsCents: presets,
      minimumAmountCents: page.minimumAmountCents,
      maximumAmountCents: page.maximumAmountCents ?? LIMITS.maxDonationCents,
      allowCustomAmount: page.allowCustomAmount,
      frequencies: page.frequencies,
      paymentMethods: page.paymentMethods,
      donorCoversFeesOption: page.donorCoversFeesOption,
    };
    const options = {
      frequencies: page.frequencies,
      paymentMethods: page.paymentMethods,
      customAmount: page.allowCustomAmount,
      donorCoversFees: page.donorCoversFeesOption,
    };
    const request = { amountCents, frequency, paymentMethod, donorCoversFees: coverFees };
    if (amountCents === null)
      return { breakdown: null, problems: ['Enter an amount in dollars and cents'] };
    const { errors, premiumViolations } = validateDonationRequest(request, settings, options);
    const all = [...errors, ...premiumViolations.map(() => 'That option is not available')];
    if (all.length) return { breakdown: null, problems: all };
    try {
      return {
        breakdown: computeDonationBreakdown({
          amountCents,
          orgFee: page.orgFee,
          platformFeeRate: page.platformFeeRate,
          donorCoversFees: coverFees,
          paymentMethod,
          recurring: frequency !== 'one_time',
        }),
        problems: [],
      };
    } catch (err) {
      return { breakdown: null, problems: [err.message] };
    }
  }, [page, presets, amountCents, frequency, paymentMethod, coverFees]);

  async function handleDonate() {
    if (!breakdown) return;
    setSubmitting(true);
    setError(null);
    try {
      const base = `${window.location.origin}/o/${encodeURIComponent(orgSlug)}/donate`;
      const data = await createDonationCheckout({
        orgId: page.org.orgId,
        amountCents,
        frequency,
        paymentMethod,
        donorCoversFees: coverFees,
        successUrl: `${base}/success`,
        cancelUrl: `${base}/cancel`,
      });
      window.location.assign(data.url);
    } catch (err) {
      setError(err.message || 'Could not start the payment. Try again.');
      setSubmitting(false);
    }
  }

  const coverEstimate = useMemo(() => {
    if (!page.donorCoversFeesOption || amountCents === null || problems.length) return null;
    try {
      const covered = computeDonationBreakdown({
        amountCents,
        orgFee: page.orgFee,
        platformFeeRate: page.platformFeeRate,
        donorCoversFees: true,
        paymentMethod,
        recurring: frequency !== 'one_time',
      });
      return covered.coveredFeesCents;
    } catch {
      return null;
    }
  }, [page, amountCents, paymentMethod, frequency, problems.length]);

  return (
    <div className="grid gap-6">
      <fieldset>
        <legend className={ui.label}>Amount</legend>
        <div className={ui.row} role="group" aria-label="Preset amounts">
          {presets.map(cents => {
            const active = !useCustom && selected === cents;
            return (
              <button
                key={cents}
                type="button"
                aria-pressed={active}
                className={active ? ui.btnPrimary : ui.btnSecondary}
                onClick={() => {
                  setUseCustom(false);
                  setSelected(cents);
                }}
              >
                {formatMoney(cents)}
              </button>
            );
          })}
          {page.allowCustomAmount && (
            <button
              type="button"
              aria-pressed={useCustom}
              className={useCustom ? ui.btnPrimary : ui.btnSecondary}
              onClick={() => setUseCustom(true)}
            >
              Other amount
            </button>
          )}
        </div>
        {page.allowCustomAmount && useCustom && (
          <label className={cn(ui.field, 'mt-3')} htmlFor={`${ids}-custom`}>
            <span className={ui.label}>Your amount (USD)</span>
            <input
              id={`${ids}-custom`}
              className={ui.input}
              inputMode="decimal"
              value={customText}
              onChange={e => setCustomText(e.target.value)}
              placeholder="25.00"
            />
            <span className={ui.hint}>
              From {formatMoney(page.minimumAmountCents)} to {formatMoney(page.maximumAmountCents)}.
            </span>
          </label>
        )}
      </fieldset>

      {page.frequencies.length > 1 && (
        <fieldset>
          <legend className={ui.label}>How often</legend>
          <div className={ui.row}>
            {page.frequencies.map(f => (
              <label key={f} className="inline-flex items-center gap-2 text-body-s">
                <input
                  type="radio"
                  name={`${ids}-frequency`}
                  className={ui.checkbox}
                  checked={frequency === f}
                  onChange={() => setFrequency(f)}
                />
                {FREQUENCY_LABELS[f]}
              </label>
            ))}
          </div>
          {frequency !== 'one_time' && (
            <p className={ui.hint}>
              You will be charged {FREQUENCY_LABELS[frequency].toLowerCase()} until you cancel. The
              receipt from Stripe explains how to cancel.
            </p>
          )}
        </fieldset>
      )}

      <fieldset>
        <legend className={ui.label}>Pay with</legend>
        {choosableMethods.length > 1 ? (
          <div className="grid gap-2">
            {choosableMethods.map(m => (
              <label key={m} className="inline-flex items-start gap-2 text-body-s">
                <input
                  type="radio"
                  name={`${ids}-method`}
                  className={cn(ui.checkbox, 'mt-1')}
                  checked={paymentMethod === m}
                  onChange={() => setPaymentMethod(m)}
                />
                <span>
                  {METHOD_LABELS[m]}
                  <span className={cn(ui.hint, 'block mt-0!')}>{METHOD_HINTS[m]}</span>
                </span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-body-s">
            {METHOD_LABELS.card}. <span className={ui.muted}>{METHOD_HINTS.card}</span>
          </p>
        )}
      </fieldset>

      {page.donorCoversFeesOption && (
        <label className="inline-flex items-start gap-2 text-body-s">
          <input
            type="checkbox"
            className={cn(ui.checkbox, 'mt-1')}
            checked={coverFees}
            onChange={e => setCoverFees(e.target.checked)}
          />
          <span>
            Cover the platform and payment processing fees
            {coverEstimate !== null && ` (adds ${formatMoney(coverEstimate)})`}
            <span className={cn(ui.hint, 'block mt-0!')}>
              So {page.org.name} receives your full donation.
            </span>
          </span>
        </label>
      )}

      {problems.length > 0 && amountCents !== null && (
        <div className={ui.alertWarning} role="status">
          {problems.join(' ')}
        </div>
      )}

      <DonationBreakdown breakdown={breakdown} frequency={frequency} title="You will pay" />

      {error && (
        <div className={ui.alertError} role="alert">
          {error}
        </div>
      )}

      <div className={ui.row}>
        <button
          type="button"
          className={ui.btnAccent}
          disabled={!breakdown || submitting}
          onClick={handleDonate}
        >
          {submitting
            ? 'Opening secure checkout...'
            : breakdown
              ? `Donate ${formatMoney(breakdown.totalCents)}`
              : 'Donate'}
        </button>
        <span className={ui.hint}>
          {signedIn
            ? 'You will get a receipt in your notifications.'
            : 'Payment is handled by Stripe. Stripe emails your receipt.'}
        </span>
      </div>
    </div>
  );
}
